import type { StickyCTAProps as Props } from './types.d.ts';
import { withChildren } from '@builder.io/react';
import { useState, useEffect, useRef } from 'react';
import { cls } from '@/utils/cls';
import RichText from '@/components/RichText';
import style from './StickyCTA.module.scss';
import { findFormWrapper } from '@/utils/findFormWrapper';
import { isBuilderPreview } from '@/utils/isBuilderPreview';
function StickyCTA({
  extendStyles,
  backgroundColor,
  displayUpperCopy,
  upperCopy,
  displayLowerCopy,
  lowerCopy,
  children
}: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    const setupFormObserver = (element: Element) => {
      observerRef.current = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          // Show sticky CTA when form is out of view AND we're below it
          const isBelowForm = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          if (isBelowForm) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        });
      }, {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      });
      observerRef.current.observe(element);
    };
    const inBuilderPreview = isBuilderPreview();
    if (inBuilderPreview) {
      setIsPreviewMode(true);
      setIsVisible(true);
    } else {
      // Add a small delay to ensure DOM is ready
      const delay = 1000;
      const timer = setTimeout(() => {
        const form = findFormWrapper();
        if (form) {
          setupFormObserver(form);
        } else {
          setIsVisible(true);
        }
      }, delay);
      return () => {
        clearTimeout(timer);
        observerRef.current?.disconnect();
      };
    }
  }, []);
  if (!children || children.length === 0) {
    return null;
  }
  const _className = cls(style.root, extendStyles);
  return <div className={cls(_className, isVisible && style.isVisible)} style={{
    backgroundColor: backgroundColor || '#ffffff'
  }} data-testid="sticky-cta" aria-hidden={!isVisible} data-sentry-component="StickyCTA" data-sentry-source-file="StickyCTA.tsx">
			{isPreviewMode && <div className={style.previewBanner}>
					📌 Sticky CTA Preview - Will appear when users scroll below the form on live site
				</div>}
			<div className={style.content}>
				{displayUpperCopy && <RichText text={upperCopy} className={style.upperCopy} />}
				<div className={style.buttons}>{children}</div>
				{displayLowerCopy && <RichText text={lowerCopy} className={style.lowerCopy} />}
			</div>
		</div>;
}
export default withChildren(StickyCTA);